import { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Card,
  Button,
  Box,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { isAdmin, isManager, isHR, isSuperAdmin } from '../../utils/share';
import Iconify from '../../components/iconify';
import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import Loading from '../../layouts/loading';
import { EscalationForm, EscalationTable, EscalationFilters } from './components';
import {
  createEscalation,
  getAllEscalations,
  deleteEscalation,
  updateEscalation,
  getEscalationById,
} from '../../services/escalationService';

const queryClient = new QueryClient();

function EscalationsContent() {
  const navigate = useNavigate();
  const [openFormModal, setOpenFormModal] = useState(false);
  const [editingEscalation, setEditingEscalation] = useState(null);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingEscalationId, setDeletingEscalationId] = useState(null);
  const user = useSelector((state) => state?.login?.user || null);
  const userId = user?.id;

  const queryClientInstance = useQueryClient();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === undefined || value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      // Reset to first page when filters change
      setPagination((prev) => ({ ...prev, page: 1 }));
      return newFilters;
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination({ page: 1, pageSize: newPageSize });
  };

  // Query for all escalations
  const { data: escalationsData = { rows: [], count: 0 }, isLoading } = useQuery({
    queryKey: ['escalations', filters, pagination],
    queryFn: async () => {
      try {
        const response = await getAllEscalations(filters, pagination);
        return response?.data?.data || { rows: [], count: 0 };
      } catch (error) {
        toast.error('Error fetching escalations');
        return { rows: [], count: 0 };
      }
    },
  });

  const escalations = escalationsData.rows || [];
  const escalationsCount = escalationsData.count || 0;

  // Mutation for creating escalation
  const createEscalationMutation = useMutation({
    mutationFn: createEscalation,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Escalation created successfully');
      setOpenFormModal(false);
      setEditingEscalation(null);
      queryClientInstance.invalidateQueries(['escalations']);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error creating escalation');
    },
  });

  // Mutation for updating escalation
  const updateEscalationMutation = useMutation({
    mutationFn: ({ id, data }) => updateEscalation(id, data),
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Escalation updated successfully');
      setOpenFormModal(false);
      setEditingEscalation(null);
      queryClientInstance.invalidateQueries(['escalations']);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error updating escalation');
    },
  });

  // Mutation for deleting escalation
  const deleteEscalationMutation = useMutation({
    mutationFn: deleteEscalation,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Escalation deleted successfully');
      queryClientInstance.invalidateQueries(['escalations']);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error deleting escalation');
    },
  });

  const handleCreateClick = () => {
    setEditingEscalation(null);
    setOpenFormModal(true);
  };

  const handleEditEscalation = useCallback(async (escalationId) => {
    try {
      const response = await getEscalationById(escalationId);
      const escalation = response?.data?.data;
      if (escalation) {
        setEditingEscalation(escalation);
        setOpenFormModal(true);
      }
    } catch (error) {
      toast.error('Error loading escalation details');
    }
  }, []);

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
    setEditingEscalation(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (values) => {
      if (editingEscalation) {
        await updateEscalationMutation.mutateAsync({ id: editingEscalation.id, data: values });
      } else {
        await createEscalationMutation.mutateAsync(values);
      }
      return true;
    },
    [createEscalationMutation, updateEscalationMutation, editingEscalation]
  );

  const handleDeleteEscalation = useCallback((escalationId) => {
    setDeletingEscalationId(escalationId);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deletingEscalationId) {
      deleteEscalationMutation.mutate(deletingEscalationId);
      setDeleteDialogOpen(false);
      setDeletingEscalationId(null);
    }
  }, [deletingEscalationId, deleteEscalationMutation]);

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Escalations',
      path: '',
    },
  ];

  return (
    <>
      <Helmet>
        <title> Escalations </title>
      </Helmet>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Escalation Management</Typography>
          <Stack direction="row" spacing={2}>
            {(isSuperAdmin() || isAdmin() || isManager() || isHR()) && (
              <Button
                variant="outlined"
                startIcon={<Iconify icon="eva:bar-chart-2-fill" />}
                onClick={() => navigate('/escalations/analytics')}
              >
                View Analytics
              </Button>
            )}
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleCreateClick}>
              Create Escalation
            </Button>
          </Stack>
        </Stack>

        <EscalationFilters filters={filters} onFilterChange={handleFilterChange} onResetFilters={handleResetFilters} />

        <Loading open={isLoading} />
        <Card>
          <Box sx={{ p: 3 }}>
            <EscalationTable
              escalations={escalations}
              onDeleteEscalation={handleDeleteEscalation}
              onEditEscalation={handleEditEscalation}
              currentUserId={userId}
              count={escalationsCount}
              page={pagination.page - 1}
              rowsPerPage={pagination.pageSize}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>

        {openFormModal && (
          <EscalationForm
            open
            onClose={handleFormModalClose}
            onSubmit={handleFormSubmit}
            escalationData={editingEscalation}
            isEdit={!!editingEscalation}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Escalation</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this escalation? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>No</Button>
            <LoadingButton
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              loading={deleteEscalationMutation.isLoading}
            >
              Yes, Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}

export default function EscalationsPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <EscalationsContent />
    </QueryClientProvider>
  );
}
